import { useEffect, useMemo, useRef } from 'react';

import FormattingService from '@core/services/FormattingService';
import PrecisionService from '@core/services/PrecisionService';

import {
  AmendmentDetailsResponse,
  AmendmentPolicyCoverDto,
  AmendmentPolicyCoverForm
} from '../types';

const buildAddressText = (p?: {
  building?: string | null;
  street?: string | null;
  city?: string | null;
  zone?: { title?: string | null } | null;
  country?: { title?: string | null } | null;
}) => {
  const building = p?.building ?? '';
  const street = p?.street ?? '';
  const zone = p?.zone?.title ?? '';
  const city = p?.city ?? '';
  const country = p?.country?.title ?? '';

  return [building, street, zone, city, country].filter(Boolean).join(', ');
};

export const useAmendmentCancellationInitials = (
  data: AmendmentDetailsResponse,
  amendmentEffectiveDate: string,
  currencySymbol: string,
  planCovers: any[],
  planCoversLoading: boolean,
  policyEffectiveDate: string,
  policyExpiryDate: string
) => {
  const details = data?.production?.getAmendmentDetails;
  const ad = details?.amendmentAmendmentDetails;
  const status = details?.status;

  const mapDtoToCoverForm = (
    dto: AmendmentPolicyCoverDto
  ): AmendmentPolicyCoverForm => {
    const planCover = planCovers?.find(
      (p) => p.cover?.id === dto.policyCoverId
    );
    const planCoverId = planCover?.id ?? dto.policyCoverId;

    const planCoverLabel = planCover
      ? `${planCover.cover?.code ?? ''}-${planCover.cover?.name ?? ''}`
      : dto.policyCover;

    const premiumType =
      (dto.premiumType || '').toUpperCase() === 'AMOUNT'
        ? 'AMOUNT'
        : (dto.premiumType || '').toUpperCase() === 'PERCENTAGE'
          ? 'PERCENTAGE'
          : '';

    const excessTypeRaw = (dto.excessType || '').toUpperCase();
    const allowed = ['AMOUNT', 'PERCENTAGE', 'DAYS'] as const;
    const excessType = allowed.includes(excessTypeRaw as any)
      ? (excessTypeRaw as any)
      : '';

    const isNew = !!dto.isNew;

    const baseSumInsured = FormattingService.parseCurrency(dto.sumInsured);
    const basePremiumAmount =
      premiumType === 'AMOUNT'
        ? FormattingService.parseCurrency(dto.premiumAmount)
        : undefined;

    const basePremiumVariance = FormattingService.parseCurrency(
      dto.premiumVariance
    );
    const basePremiumPercentage =
      premiumType === 'PERCENTAGE'
        ? PrecisionService.multiplyBy100(dto.premiumPercentage as any)
        : undefined;

    const excessAmount =
      excessType === 'AMOUNT'
        ? FormattingService.parseCurrency(dto.excess)
        : undefined;
    const excessPercentage =
      excessType === 'PERCENTAGE'
        ? PrecisionService.multiplyBy100(dto.excess as any)
        : undefined;
    const excessDays =
      excessType === 'DAYS' && dto.excess != null
        ? Number(dto.excess)
        : undefined;

    const newSumInsuredValue =
      dto.newSumInsured != null
        ? FormattingService.parseCurrency(dto.newSumInsured)
        : baseSumInsured;

    const newPremiumAmountValue =
      premiumType === 'AMOUNT' && dto.newPremiumAmount != null
        ? FormattingService.parseCurrency(dto.newPremiumAmount)
        : basePremiumAmount;

    const newPremiumPercentageValue =
      premiumType === 'PERCENTAGE' && dto.newPremiumPercentage != null
        ? PrecisionService.multiplyBy100(dto.newPremiumPercentage as any)
        : basePremiumPercentage;

    const newExcessAmountValue =
      excessType === 'AMOUNT' && dto.newExcess != null
        ? FormattingService.parseCurrency(dto.newExcess)
        : excessAmount;

    const newExcessPercentageValue =
      excessType === 'PERCENTAGE' && dto.newExcess != null
        ? PrecisionService.multiplyBy100(dto.newExcess as any)
        : excessPercentage;

    const newExcessDaysValue =
      excessType === 'DAYS' && dto.newExcess != null
        ? Number(dto.newExcess)
        : excessDays;
    console.log(dto, 'HELLO');

    return {
      id: dto.id,
      policyPlanCoverId: planCoverId,
      policyCover: planCoverId ?? '',
      policyCoverName: planCoverLabel,
      isMain: !!dto.isMain,
      isMandatory: !!dto.isMandatory,
      isEditable: dto.isEditable ?? true,
      isNew,
      isRemoved: !!dto.isRemoved,
      coverEffectiveFrom:
        dto.coverEffectiveFromVariance || policyEffectiveDate || undefined,
      coverEffectiveTo:
        dto.coverEffectiveToVariance || policyExpiryDate || undefined,
      sumInsuredIsAdditive: !!dto.additiveSumInsured,
      coverSumInsured: baseSumInsured,
      coverPremiumType: premiumType,
      coverPremiumPercentage: basePremiumPercentage,
      coverPremiumAmount: basePremiumAmount,
      coverPremiumVariance: basePremiumVariance,
      excessOnClaimType: excessType,
      excessOnClaimPercentage: excessPercentage,
      excessOnClaimAmount: excessAmount,
      excessOnClaimDays: excessDays,
      proRataRatio: undefined,
      newSumInsured: newSumInsuredValue,
      newPremiumPercentage: newPremiumPercentageValue,
      newPremiumAmount: newPremiumAmountValue,
      newExcessDays: newExcessDaysValue,
      newExcessAmount: newExcessAmountValue,
      newExcessPercentage: newExcessPercentageValue
    };
  };
  console.log(details);
  const initialCovers = useMemo(() => {
    // Wait for planCovers to load before processing covers to ensure correct IDs
    if (planCoversLoading) {
      return [];
    }

    const list = details?.policyCovers ?? [];
    console.log(list);
    const mapped = list.map((dto: AmendmentPolicyCoverDto) =>
      mapDtoToCoverForm(dto)
    );

    return mapped.sort((a, b) => {
      if (a.isNew && !b.isNew) return 1;
      if (!a.isNew && b.isNew) return -1;

      if (a.isNew && b.isNew) {
        const nameA = (a.policyCoverName || '').toLowerCase();
        const nameB = (b.policyCoverName || '').toLowerCase();

        return nameA.localeCompare(nameB);
      }

      return 0;
    });
  }, [
    details?.policyCovers,
    planCovers,
    planCoversLoading,
    policyEffectiveDate,
    policyExpiryDate
  ]);

  const initialValues = useMemo<any>(() => {
    return {
      amendmentDetails: {
        amendmentType: ad?.amendmentType ?? '',
        amendmentSubType: ad?.amendmentSubType?.id ?? '',
        amendmentIssueDate: ad?.amendmentIssueDate ?? '',
        amendmentEffectiveDate,
        amendmentReason: ad.amendmentReason
      },
      chargesAndCost: {
        amendmentCost: details?.costAndChargesDetails?.amendmentCost,
        chargesPercentage:
          details?.costAndChargesDetails?.currentChargesPercentage,
        tpaFees: details?.costAndChargesDetails?.currentTPAFees,
        newChargesPercentage: details?.costAndChargesDetails?.chargesPercentage,
        newTPAFees: details?.costAndChargesDetails?.tpaFees,

        // Cancellation specific fields
        basicPremium: Number(details?.offerDetails?.basicPremium),
        basicPremiumVariance: Number(
          details?.policyQuoteVariance?.basicPremium
        ),
        chargesAmount: Number(details?.offerDetails?.charges),
        chargesAmountVariance: Number(details?.policyQuoteVariance?.charges),
        tpaFeesVariance: Number(details?.policyQuoteVariance?.tpaFees),
        cancellationCost: Number(details?.policyQuoteVariance?.policyCost)
      },
      insuredPolicyMedicals: details?.insuredsDetails ?? [],
      totalCount: details?.insuredsDetails?.length ?? 0,
      status: status,
      documents: details?.documents ?? undefined,
      covers: initialCovers
    };
  }, [
    ad,
    amendmentEffectiveDate,
    details?.costAndChargesDetails,
    details?.policyDetails?.currency?.id,
    details?.insuredsDetails,
    currencySymbol,
    status,
    initialCovers
  ]);

  const renderCountRef = useRef(0);
  useEffect(() => {
    renderCountRef.current++;
  }, [initialValues]);

  return { initialValues };
};
